import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Activity, Database, Clock, AlertTriangle, RefreshCw,
  CheckCircle2, XCircle, TrendingUp,
  Flag, CreditCard, Users as UsersIcon,
} from 'lucide-react';
import { superApi } from './SuperAdminAuth';

// ---------------------------------------------------------------------------
// System Health
// ---------------------------------------------------------------------------
export const SuperSystemHealthPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pushHealth, setPushHealth] = useState(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const [h, ph] = await Promise.all([
        superApi.get('/super/system-health'),
        superApi.get('/sales-channels/push-health').catch(() => ({ data: null })),
      ]);
      setData(h.data);
      setPushHealth(ph.data);
    } catch (e) {
      toast.error('Failed to load system health');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); const t = setInterval(() => load(true), 30000); return () => clearInterval(t); }, []);

  if (loading) return <div className="p-8 text-slate-400">Loading system health…</div>;
  if (!data) return <div className="p-8 text-slate-400">No data.</div>;

  const mongoOk = data.mongodb?.ok;
  const schedOk = data.scheduler?.running;
  const errRate24 = data.journey_stats?.last_24h?.total
    ? ((data.journey_stats.last_24h.errors / data.journey_stats.last_24h.total) * 100).toFixed(1)
    : '0.0';

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="super-system-health">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Activity className="w-6 h-6 text-amber-400" />
          <h1 className="text-2xl font-semibold text-white">System Health</h1>
        </div>
        <button
          onClick={() => load()}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm disabled:opacity-50"
          data-testid="refresh-health-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <HealthStatusCard
          label="MongoDB"
          ok={mongoOk}
          Icon={Database}
          detail={mongoOk ? `v${data.mongodb.version} · ${data.mongodb.connections_current}/${data.mongodb.connections_available} conns` : (data.mongodb?.error || 'Unreachable')}
        />
        <HealthStatusCard
          label="Scheduler"
          ok={schedOk}
          Icon={Clock}
          detail={schedOk ? `${data.scheduler.jobs.length} jobs running` : 'Not running'}
        />
        <HealthStatusCard
          label="Journey error rate (24h)"
          ok={parseFloat(errRate24) < 5}
          Icon={TrendingUp}
          detail={`${errRate24}% · ${data.journey_stats?.last_24h?.errors || 0} errors of ${data.journey_stats?.last_24h?.total || 0}`}
        />
        <HealthStatusCard
          label="Inventory Pushes (24h)"
          ok={pushHealth ? pushHealth.permanent_failures === 0 : true}
          Icon={TrendingUp}
          detail={pushHealth
            ? `${pushHealth.total} total · ${pushHealth.succeeded} ok · ${pushHealth.failed} failed${pushHealth.permanent_failures > 0 ? ` · ${pushHealth.permanent_failures} permanent` : ''}`
            : 'No data'}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Scheduled jobs */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-400" /> Scheduled Jobs
          </h2>
          <div className="space-y-2 max-h-80 overflow-auto" data-testid="scheduler-jobs-list">
            {(data.scheduler?.jobs || []).length === 0 ? (
              <div className="text-xs text-slate-500">No scheduled jobs</div>
            ) : (
              data.scheduler.jobs.map(j => (
                <div key={j.id} className="py-2 border-b border-slate-800 last:border-0">
                  <div className="text-sm text-slate-200">{j.name || j.id}</div>
                  <div className="text-xs text-slate-500 font-mono mt-0.5">
                    Next: {j.next_run ? new Date(j.next_run).toLocaleString() : '—'}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Top error companies */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-400" /> Top Error Companies (24h)
          </h2>
          <div className="space-y-2" data-testid="top-error-companies">
            {(data.top_error_companies_24h || []).length === 0 ? (
              <div className="text-xs text-emerald-400 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> No journey errors in the last 24 hours
              </div>
            ) : (
              data.top_error_companies_24h.map((c, i) => (
                <div key={c.company_id || i} className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0">
                  <span className="text-sm text-slate-200">{c.company_name}</span>
                  <span className="text-sm font-mono text-rose-400">{c.errors} errors</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Collection counts */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mt-6">
        <h2 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <Database className="w-4 h-4 text-amber-400" /> Collection Sizes
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 text-sm">
          {Object.entries(data.collections || {}).map(([name, count]) => (
            <div key={name} className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2">
              <div className="text-xs text-slate-500">{name}</div>
              <div className="text-slate-100 font-mono">{count >= 0 ? count.toLocaleString() : '—'}</div>
            </div>
          ))}
        </div>
      </div>

      <SlowQueryPanel />

      <p className="text-xs text-slate-600 mt-6">Generated at {new Date(data.generated_at).toLocaleTimeString()} · Auto-refresh every 30s</p>
    </div>
  );
};

const HealthStatusCard = ({ label, ok, Icon, detail }) => (
  <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-slate-400" />
        <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
      </div>
      {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-rose-400" />}
    </div>
    <div className={`text-lg font-semibold mt-2 ${ok ? 'text-emerald-300' : 'text-rose-300'}`}>
      {ok ? 'Healthy' : 'Degraded'}
    </div>
    <div className="text-xs text-slate-500 mt-1 font-mono">{detail}</div>
  </div>
);

// --- Slow Query Watchdog panel -------------------------------------------
const SlowQueryPanel = () => {
  const [state, setState] = useState({ current: [], recent: [], err: null, loading: true });
  const load = async () => {
    try {
      const r = await superApi.get('/super/slow-queries?threshold_ms=1000');
      setState({
        current: r.data.current_operations || [],
        recent: r.data.recent_log || [],
        err: r.data.current_error,
        loading: false,
      });
    } catch {
      setState(s => ({ ...s, loading: false, err: 'Failed to load' }));
    }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mt-6" data-testid="slow-query-panel">
      <h2 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-400" /> Slow Query Watchdog <span className="text-xs font-normal text-slate-500">(&gt; 1s)</span>
      </h2>
      {state.loading ? (
        <div className="text-xs text-slate-500">Loading…</div>
      ) : state.err ? (
        <div className="text-xs text-rose-400 font-mono">{state.err}</div>
      ) : (state.current.length === 0 && state.recent.length === 0) ? (
        <div className="text-xs text-emerald-400 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> No slow queries in the watchdog log
        </div>
      ) : (
        <div className="space-y-3 text-xs" data-testid="slow-query-list">
          {state.current.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Currently running</div>
              {state.current.slice(0, 5).map((op, i) => (
                <div key={i} className="font-mono text-slate-300 py-1">
                  <span className="text-rose-400">{op.secs_running}s</span> {op.ns} · {op.op} · plan={op.planSummary || '—'}
                </div>
              ))}
            </div>
          )}
          {state.recent.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 mt-3">Recent (last 50)</div>
              <div className="max-h-64 overflow-auto">
                {state.recent.slice(0, 25).map((q, i) => (
                  <div key={i} className="py-1 border-b border-slate-800 last:border-0">
                    <div className="flex justify-between">
                      <span className="text-slate-300 font-mono">{q.ns}</span>
                      <span className="text-rose-400 font-mono">{q.ms_running}ms</span>
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono">
                      {q.op} · plan={q.plan_summary || '—'}
                      {q.filter_keys?.length ? ` · filter={${q.filter_keys.join(', ')}}` : ''}
                    </div>
                    <div className="text-[10px] text-slate-600">{new Date(q.observed_at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const HealthStatusCardDUPLICATE = () => null;

// ---------------------------------------------------------------------------
// Feature Flags — matrix across tenants// ---------------------------------------------------------------------------
// Feature Flags — matrix across tenants
// ---------------------------------------------------------------------------
export const SuperFeatureFlagsPage = () => {
  const [data, setData] = useState({ companies: [], known_flags: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await superApi.get('/super/feature-flags');
      setData(r.data);
    } catch {
      toast.error('Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (company, flag, current) => {
    const next = { ...(company.feature_flags || {}), [flag]: !current };
    const key = `${company.id}:${flag}`;
    setBusy(b => ({ ...b, [key]: true }));
    try {
      await superApi.put(`/super/companies/${company.id}/feature-flags`, { flags: next });
      setData(d => ({
        ...d,
        companies: d.companies.map(c => c.id === company.id ? { ...c, feature_flags: next } : c),
      }));
      toast.success(`${flag}: ${next[flag] ? 'enabled' : 'disabled'} for ${company.name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Toggle failed');
    } finally {
      setBusy(b => ({ ...b, [key]: false }));
    }
  };

  if (loading) return <div className="p-8 text-slate-400">Loading feature flags…</div>;

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto" data-testid="super-feature-flags">
      <div className="flex items-center gap-2 mb-6">
        <Flag className="w-6 h-6 text-amber-400" />
        <h1 className="text-2xl font-semibold text-white">Feature Flags</h1>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950 border-b border-slate-800">
              <tr>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-500 sticky left-0 bg-slate-950">Company</th>
                {data.known_flags.map(f => (
                  <th key={f.key} className="text-center px-3 py-3 text-xs uppercase tracking-wider text-slate-500 whitespace-nowrap" title={f.description}>
                    {f.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.companies.map((c, i) => (
                <tr key={c.id} className={i % 2 === 0 ? 'bg-slate-900' : 'bg-slate-900/50'}>
                  <td className="px-4 py-2.5 text-slate-200 sticky left-0 bg-inherit whitespace-nowrap">{c.name}</td>
                  {data.known_flags.map(f => {
                    const on = !!c.feature_flags?.[f.key];
                    const key = `${c.id}:${f.key}`;
                    return (
                      <td key={f.key} className="px-3 py-2.5 text-center">
                        <button
                          onClick={() => toggle(c, f.key, on)}
                          disabled={busy[key]}
                          className={`w-10 h-5 rounded-full relative transition-colors ${on ? 'bg-amber-500' : 'bg-slate-700'} disabled:opacity-50`}
                          data-testid={`ff-toggle-${c.id}-${f.key}`}
                          aria-label={`Toggle ${f.label} for ${c.name}`}
                        >
                          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${on ? 'left-5' : 'left-0.5'}`} />
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 bg-slate-900 border border-slate-800 rounded-xl p-4">
        <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Flag reference</h3>
        <dl className="space-y-2 text-sm">
          {data.known_flags.map(f => (
            <div key={f.key} className="grid grid-cols-12 gap-3">
              <dt className="col-span-4 text-slate-300 font-mono text-xs">{f.key}</dt>
              <dd className="col-span-8 text-slate-400 text-xs">{f.description}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Billing — read-only overview
// ---------------------------------------------------------------------------
export const SuperBillingPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await superApi.get('/super/billing/overview');
      setData(r.data);
    } catch {
      toast.error('Failed to load billing');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-8 text-slate-400">Loading billing…</div>;
  if (!data) return null;

  const { companies = [], summary = {} } = data;
  const fmt = v => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v || 0);

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="super-billing">
      <div className="flex items-center gap-2 mb-6">
        <CreditCard className="w-6 h-6 text-amber-400" />
        <h1 className="text-2xl font-semibold text-white">Billing Overview</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total companies" value={summary.total_companies || 0} Icon={UsersIcon} />
        <StatCard label="MRR" value={fmt(summary.mrr_total)} Icon={TrendingUp} />
        <StatCard label="Suspended" value={summary.suspended || 0} Icon={XCircle} danger />
        <StatCard label="Plans" value={Object.entries(summary.plan_counts || {}).map(([p, n]) => `${p}:${n}`).join(' · ') || 'free:' + (summary.total_companies || 0)} Icon={Flag} />
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-950 border-b border-slate-800">
            <tr>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Company</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Plan</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Status</th>
              <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-slate-500">MRR</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Next invoice</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((c, i) => (
              <tr key={c.id} className={`${i % 2 === 0 ? 'bg-slate-900' : 'bg-slate-900/50'} hover:bg-slate-800 cursor-pointer`} onClick={() => window.location.href = `/superadmin/billing/${c.id}`} data-testid={`billing-row-${c.id}`}>
                <td className="px-4 py-2.5 text-slate-200">
                  <div>{c.name}</div>
                  <div className="text-xs text-slate-500">{c.billing_method || '—'}</div>
                </td>
                <td className="px-4 py-2.5 text-slate-300">{c.plan}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    c.status === 'active' ? 'bg-emerald-500/10 text-emerald-300' :
                    c.status === 'suspended' ? 'bg-rose-500/10 text-rose-300' :
                    'bg-slate-700 text-slate-300'
                  }`}>{c.status}</span>
                </td>
                <td className="px-4 py-2.5 text-right text-slate-200 font-mono">{fmt(c.mrr)}</td>
                <td className="px-4 py-2.5 text-slate-400 text-xs">{c.next_invoice_at ? new Date(c.next_invoice_at).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-600 mt-4">
        Read-only view. Phase 2: payment provider integration (Stripe / MamoPay) for invoice drill-in, refunds, and plan changes.
      </p>
    </div>
  );
};

const StatCard = ({ label, value, Icon, danger }) => (
  <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
      <Icon className={`w-4 h-4 ${danger ? 'text-rose-400' : 'text-slate-400'}`} />
    </div>
    <div className={`text-xl font-semibold mt-2 ${danger ? 'text-rose-300' : 'text-white'}`}>{value}</div>
  </div>
);

export default SuperBillingPage;
